import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { IconName, IconSizes, PearlIcon } from 'dbi-pearl-ui-kit';
import {
    WelcomeContainer,
    IntroBlock,
    WelcomeTitle,
    WelcomeIntro,
    TrialAccent,
    FeatureGrid,
    FeatureTile,
    FeatureIconCircle,
    FeatureCopy,
    FeatureTitle,
    FeatureDescription,
    TrialError,
    TrialFooterActions,
    ContinueTrialButton,
    PickPlanButton,
    ContinueArrowIcon,
} from './PremiumTrialWelcome.styled';
import AiProductivityFeatureIcon from './AiProductivityFeatureIcon';

const FEATURE_ITEMS = [
    {
        key: 'verifiedListing',
        icon: IconName.CHECKMARK_CIRCLE,
    },
    {
        key: 'connectedEmail',
        icon: IconName.EMAIL,
    },
    {
        key: 'leadIntelligence',
        icon: IconName.MESSAGE,
    },
    {
        key: 'connectedCalendar',
        icon: IconName.CALENDAR,
    },
    {
        key: 'appointmentBooking',
        icon: IconName.CLOCK,
    },
    {
        key: 'aiProductivity',
        customIcon: true,
    },
];

export const PremiumTrialFooterActions = ({
    onContinueTrial,
    onPickPlan,
    trialLoading,
    className = '',
}) => {
    const { t } = useTranslation();

    return (
        <TrialFooterActions className={className}>
            <ContinueTrialButton
                type="button"
                data-testid="continue-trial-button"
                onClick={onContinueTrial}
                disabled={trialLoading}
            >
                {t('Forms.BasicDetails.PremiumTrialWelcome.Continue')}
                <ContinueArrowIcon aria-hidden>
                    <PearlIcon iconName={IconName.ARROW_RIGHT} size={IconSizes.SM} />
                </ContinueArrowIcon>
            </ContinueTrialButton>
            <PickPlanButton
                type="button"
                data-testid="pick-plan-button"
                onClick={onPickPlan}
                disabled={trialLoading}
            >
                {t('Forms.BasicDetails.PremiumTrialWelcome.PickPlan')}
            </PickPlanButton>
        </TrialFooterActions>
    );
};

const PremiumTrialWelcome = ({ trialError, onContinueTrial, onPickPlan, trialLoading }) => {
    const { t } = useTranslation();

    return (
        <WelcomeContainer data-testid="premium-trial-welcome">
            <IntroBlock>
                <WelcomeTitle>{t('Forms.BasicDetails.PremiumTrialWelcome.Title')}</WelcomeTitle>
                <WelcomeIntro>
                    <Trans
                        i18nKey="Forms.BasicDetails.PremiumTrialWelcome.Intro"
                        components={{ accent: <TrialAccent /> }}
                    />
                </WelcomeIntro>
            </IntroBlock>
            <FeatureGrid>
                {FEATURE_ITEMS.map(({ key, icon, customIcon }) => (
                    <FeatureTile key={key}>
                        <FeatureIconCircle $isAiProductivity={customIcon}>
                            {customIcon ? (
                                <AiProductivityFeatureIcon />
                            ) : (
                                <PearlIcon iconName={icon} size={IconSizes.LG} />
                            )}
                        </FeatureIconCircle>
                        <FeatureCopy>
                            <FeatureTitle>
                                {t(`Forms.BasicDetails.PremiumTrialWelcome.Features.${key}.title`)}
                            </FeatureTitle>
                            <FeatureDescription>
                                {t(
                                    `Forms.BasicDetails.PremiumTrialWelcome.Features.${key}.description`
                                )}
                            </FeatureDescription>
                        </FeatureCopy>
                    </FeatureTile>
                ))}
            </FeatureGrid>
            {trialError ? <TrialError role="alert">{trialError}</TrialError> : null}
            {onContinueTrial && onPickPlan ? (
                <PremiumTrialFooterActions
                    className="premiumTrialWelcomeMobileFooter"
                    onContinueTrial={onContinueTrial}
                    onPickPlan={onPickPlan}
                    trialLoading={trialLoading}
                />
            ) : null}
        </WelcomeContainer>
    );
};

export default PremiumTrialWelcome;
